import { useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useGetscriptTypesQuery, useSaveScriptMutation } from "~/services/script";
import { useGetCloudEnvironmentsQuery } from "~/services/cloudenvironment";
import { FormScript, FormScriptResponse } from "~/models/Commons ";
import { useTranslation } from "react-i18next";

export const UseFormScript = (
  editedItem: FormScriptResponse | null,
  onCloseModal: () => void,
  refetch: () => void,
  onSuccess?: (title: string, message: string, response: any) => void,
  onError?: (title: string, message: string) => void,
) => {
  const isSubmittingRef = useRef(false);
  const { t } = useTranslation();
  const [saveScript, { isLoading, isError }] = useSaveScriptMutation();

  const { data: cloudEnvData  } = useGetCloudEnvironmentsQuery({
    page: 0,
    size: 100,
    active: true,
  });
  const{ data: scriptTypesData, refetch: refetchScriptTypes } = useGetscriptTypesQuery({page: 0, size: 9999, active: true});

  const {
    formState: { errors },
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    watch,
  } = useForm<FormScript>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      title: editedItem?.title ?? "",
      description: editedItem?.description ?? "",
      location: editedItem?.location ?? "",
      cloudEnvironmentId: undefined,
      scriptTypeId: undefined,
      outputTemplateId: undefined,
      manual: false,
      script: null,
      configFiles: null,
    },
  });

  const currentScript = watch("script");
  const currentConfigFiles = watch("configFiles");

  const BLOCKED_EXTENSIONS = new Set(["txt", "pdf", "doc", "docx"]);

  const onScriptDrop = useCallback(
    ([file]: File[]) => {
      if (!file) {
        setError("script", {
          type: "manual",
          message: t("SelectValidFile"),
        });
        return;
      }

      const extension = file.name.includes(".")
        ? file.name.split(".").pop()!.toLowerCase()
        : null;

      if (!extension || BLOCKED_EXTENSIONS.has(extension)) {
        setError("script", {
          type: "manual",
          message: t("SelectValidFile"),
        });
        return;
      }

      setValue("script", file);
      clearErrors("script");
    },
    [setValue, clearErrors, setError],
  );

  // Config files drop (optional)
  const onConfigDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles?.[0] ?? null;

      setValue("configFiles", file);
      if (file) clearErrors("configFiles");
    },
    [setValue, clearErrors],
  );

  const handleError = (error: any) => {
    onError?.(
      `${error?.data?.title} (${error?.data?.status})` ,
      error?.data?.detail,
    );
  };

const submitForm = async (data: FormScript) => {
  if (isSubmittingRef.current || isLoading) return;

  isSubmittingRef.current = true;

  try {
    const response = await saveScript({
      data,
    }).unwrap();

    onSuccess?.(
      t("MessageOperationCompletedSuceesuflly"),
      response?.message || "",
      response,
    );

    onCloseModal();
  } catch (error: any) {
    onError?.(
      `${error?.data?.title} (${error?.data?.status})`,
      error?.data?.detail,
    );
    handleError(error);
  } finally {
    refetch();
    isSubmittingRef.current = false;
  }
};

  return {
    handleSubmit,
    control,
    errors,
    currentScript,
    currentConfigFiles,
    onScriptDrop,
    onConfigDrop,
    submitForm,
    isLoading,
    isError,
    cloudEnvData,
    scriptTypesData,
    onSuccess,
    onError,
    refetchScriptTypes,
    watch,
    setValue,
  };
};
